<template>
	<JBox class="s-bg-white s-text-md s-border s-border-gray-300 s-rounded">
		<!-- File main top bar -->
		<FileListingTopBar
			:bucketName="bucketName"
			:path="computedPath"
			:hasCutItem="cutItem ? true : false"
			:isSearching="isSearching"
			:enableDownloadAsZip="enableDownloadAsZip"
			:hasSelectedItems="itemsSelected.length > 0"
			@path:clicked="pathClick"
			@action:refresh="fetchFiles"
			@action:search="toggleSearch"
			@action:createFolder="toggleCreateFolderModal"
			@action:uploadFile="toggleUploadFileModal"
			@action:pasteFile="onPaste(prefix)"
			@action:downloadAsZip="downloadAsZip"
			@loadBuckets="$emit('loadBuckets')"
		/>

		<JSpinner v-if="fileLoading" variant="indigo" type="dots" class="s-mx-auto s-mt-20" :delay="0" />

		<!-- Zip Download Loading -->
		<JBox
			v-if="isZipDownloading"
			class="s-fixed s-inset-0 s-bg-black s-bg-opacity-50 s-flex s-items-center s-justify-center s-z-50"
		>
			<JBox class="s-bg-white s-p-6 s-rounded-lg s-shadow-xl s-text-center">
				<JSpinner variant="indigo" type="dots" class="s-mx-auto s-mb-4" :delay="0" />
				<div class="s-text-lg s-font-medium s-text-gray-700">{{ $t("core.preparingZipDownload") }}</div>
			</JBox>
		</JBox>

		<!-- Listing of files and folders -->
		<JBox class="s-max-w-full s-overflow-y-auto" style="min-height: 40rem;">
			<table v-if="!fileLoading && !isSearching" class="s-w-full">
				<thead>
					<tr class="s-font-medium s-border-b s-border-t s-border-gray-200 s-text-gray-900 s-bg-gray-50">
						<td class="s-p-4">
							{{ $t("core.name") }}
						</td>
						<td class="s-p-4">
							{{ $t("core.size") }}
						</td>
						<td class="s-p-4">
							{{ $t("core.modified") }}
						</td>
						<td class="s-p-4 s-text-right">
							{{ $t("core.actions") }}
						</td>
					</tr>
				</thead>
				<tbody>
					<!-- To remove the selected item, we select the item again -->
					<tr
						v-for="(item, di) in displayList"
						:key="di"
						class="s-leading-tight s-border-b s-border-gray-100"
						:class="[
							{
								's-bg-indigo-100': selectItem && selectItem.name === item.name && selectItem.path === item.path,
							},
							{
								's-bg-indigo-200': itemsSelected.findIndex(file => file.path === item.path) !== -1,
							},
						]"
						@click="onClick(item)"
						@dblclick="goToPath(item)"
					>
						<td class="s-px-4 s-py-6">
							<JFlex
								class="s-cursor-pointer s-font-medium s-items-center"
								:class="[
									{
										's-text-gray-700':
											item.type === 'folder' && (!selectItem || selectItem.name !== item.name || selectItem.path !== item.path),
									},
									{ 's-opacity-50': item.isCut },
								]"
							>
								<svg
									v-if="item.type === 'folder'"
									width="20"
									height="20"
									viewBox="0 0 20 20"
									class="s-mr-2 s-w-4 s-h-4 s-flex-shrink-0"
									:class="[
										{
											's-text-gray-900':
												item.type === 'folder' && (!selectItem || selectItem.name !== item.name || selectItem.path !== item.path),
										},
									]"
									fill="currentColor"
								>
									<path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z"></path>
								</svg>

								<span>
									{{ item.name }}
								</span>
							</JFlex>
						</td>
						<td class="s-px-4 s-py-2">
							{{ item.size ? formatBytes(item.size) : "" }}
						</td>
						<td class="s-px-4 s-py-2">{{ item.lastModifiedAt || "" }}</td>
						<td class="s-px-4 s-py-2">
							<JFlex class="s-items-center s-justify-end s-h-full">
								<!-- Popover menu -->
								<FileListingFileActions
									v-if="item.type === 'file'"
									@action:edit="toggleUpdateFileModal"
									@action:cut="onCut(item)"
									@action:downloadFile="downloadFile(item)"
									@action:deleteFile="onDelete(item)"
								/>
								<!-- Delete Folder -->
								<button
									v-if="item.type === 'folder'"
									class="s-pl-2 s-appearance-none focus:s-outline-none"
									:title="$t('core.remove')"
									@click.stop="onDelete(item)"
								>
									<svg width="18" height="18" viewBox="0 0 24 24" class="s-text-red-600">
										<path
											fill="currentColor"
											d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"
										></path>
										<path d="M0 0h24v24H0z" fill="none"></path>
									</svg>
								</button>
							</JFlex>
						</td>
					</tr>
				</tbody>
			</table>

			<!-- Search -->
			<FileListingSearch
				v-if="isSearching"
				:bucketName="bucketName"
				:allowedPrefixes="[prefix]"
				:enableDownloadAsZip="enableDownloadAsZip"
				@file:selected="handleFileSelectFromSearch($event)"
				@items:selected="handleItemsSelectedFromSearch($event)"
			/>
		</JBox>

		<!-- Selected items from search or file listing -->
		<JFlex
			v-if="isSearching && enableDownloadAsZip && itemsSelected.length > 0"
			class="s-justify-between s-px-4 s-py-2 s-border-t s-border-gray-200 s-bg-gray-50"
		>
			<div class="s-text-gray-700">{{ itemsSelected.length }} {{ itemsSelected.length === 1 ? "file" : "files" }} selected</div>
		</JFlex>

		<!-- Selected Folder/File Details -->
		<JFlex
			v-if="!isSearching && (selectItem || itemsSelected.length)"
			class="s-justify-between s-px-4 s-py-2 s-border-t s-border-gray-200 s-bg-gray-50"
		>
			<FileListingDetails class="s-w-10/12" :item="selectItem" />
			<!-- Action to pick file -->
			<JButton v-if="isFilePicker && itemsSelected.every(file => file.type === 'file')" @click="onPickFile">{{
				$t("core.selectFile")
			}}</JButton>
		</JFlex>

		<!-- Create Folder Modal -->
		<CModal :isVisible="isCreateFolderModalOpen" @overlay:click="toggleCreateFolderModal">
			<div class="s-bg-white">
				<div class="s-p-5">
					<div class="s-mb-2 s-text-lg s-font-semibold">{{ $t("core.createFolder") }}</div>
					<JInputText v-model="createFolderName" placeholder="Folder Name" />
					<JFlex class="s-justify-between s-mt-6">
						<JButton variant="white" @click="toggleCreateFolderModal">{{ $t("core.cancel") }}</JButton>
						<JButton variant="default-light" @click="createFolder">{{ $t("core.create") }}</JButton>
					</JFlex>
				</div>
			</div>
		</CModal>

		<!-- Upload File Modal -->
		<CModal :isVisible="isUploadModalOpen" @overlay:click="toggleUploadFileModal">
			<div class="s-bg-white">
				<div class="s-text-xl s-font-semibold s-text-black s-px-4 s-pt-3">
					{{ $t("core.fileUpload") }}
				</div>
				<div class="s-px-6 s-pb-6 s-pt-3">
					<CFilesUpload :prefix="prefix" @cancel="toggleUploadFileModal" @file:uploaded="afterFileUpload" />
				</div>
			</div>
		</CModal>

		<!-- Delete confirmation Modal -->
		<CModal :isVisible="isDeleteModalOpen" @overlay:click="toggleDeleteModal">
			<div class="s-bg-white">
				<div class="s-p-5">
					<div class="s-mb-2 s-text-lg s-font-semibold">{{ $t("core.deleteText") }}</div>
					<div class="s-mt-3 s-mb-1">{{ $t("core.confirmDelete") }}</div>
					<JInputText v-model="deleteConfirmText" />
					<JFlex class="s-justify-between s-mt-6">
						<JButton variant="white" @click="toggleDeleteModal">{{ $t("core.cancel") }}</JButton>
						<JButton variant="default-light" v-if="deleteConfirmText === 'DELETE'" @click="onDeleteConfirm">{{
							$t("core.delete")
						}}</JButton>
					</JFlex>
				</div>
			</div>
		</CModal>

		<!-- Edit File Modal -->
		<CModal :isVisible="isUpdateFileModalOpen" @overlay:click="toggleUpdateFileModal">
			<div class="s-bg-white">
				<div class="s-text-xl s-font-semibold s-text-black s-px-6 s-py-3">
					{{ $t("core.edit") }}
				</div>
				<div class="s-px-6 s-pb-6">
					<FileListingFileEdit :file="selectItem" @cancel="toggleUpdateFileModal" @file:updated="afterFileUpdate" />
				</div>
			</div>
		</CModal>

		<CMessage />
	</JBox>
</template>

<script>
import api from "@/api/api"
import { query, mutation } from "../../api/queries"
import dayjs from "dayjs"
import axios from "axios"

import FileListingTopBar from "./FileListingTopBar"
import FileListingDetails from "./FileListingDetails"
import FileListingSearch from "./FileListingSearch"
import FileListingFileEdit from "./FileListingFileEdit"
import FileListingFileActions from "./FileListingFileActions"

export default {
	props: {
		bucketName: {
			type: String,
			required: true,
		},
		isFilePicker: {
			type: Boolean,
			default: false,
		},
	},

	components: {
		FileListingTopBar,
		FileListingDetails,
		FileListingSearch,
		FileListingFileEdit,
		FileListingFileActions,
	},

	data() {
		return {
			files: [],
			fileLoading: false,
			prefix: "",
			selectItem: null,
			itemsSelected: [],
			cutItem: null,
			isCreateFolderModalOpen: false,
			isUploadModalOpen: false,
			deleteConfirmText: null,
			isDeleteModalOpen: false,
			isUpdateFileModalOpen: false,
			createFolderName: "",
			isSearching: false,
			maxPathHistory: 10,
			pathHistory: [],
			sortField: "created_date",
			sortDirection: "", // Default to descending (newest first)
			isZipDownloading: false,
		}
	},
	computed: {
		folderList() {
			let list = this?.files?.commonPrefixes ?? []
			list = list.map(item => {
				const arr = item.split("/")
				return {
					type: "folder",
					path: item,
					name: arr[arr.length - 2],
					...item,
				}
			})
			return list
		},

		fileList() {
			let list = this?.files?.objectSummaries ?? []
			list = list
				.filter(item => {
					return !!item.fileName
				})
				.map(item => {
					if (!item.fileName) return false
					return {
						...item,
						type: "file",
						path: item.key,
						name: item.fileName,
						lastModifiedAt: item.lastModifiedAt ? dayjs(item.lastModifiedAt).format("DD/MM/YYYY HH:mm:ss") : null,
					}
				})
			return list
		},
		displayList() {
			return [...this.folderList, ...this.fileList]
		},
		computedPath() {
			let path = ""
			const array =
				this.prefix.split("/").map(step => {
					path = path + step + "/"
					return {
						name: step,
						key: path,
					}
				}) || []
			array.pop()
			return array
		},
		enableDownloadAsZip() {
			return this.$store.state?.featureFlags?.filesExplorer?.enableDownloadAsZip || false
		},
	},
	async mounted() {
		await this.fetchFiles()
	},
	methods: {
		onClick(item) {
			this.selectItem = item
			// If the file item already in selected list, remove it out
			if (this.itemsSelected.findIndex(file => file.path === item.path) !== -1) {
				this.itemsSelected = this.itemsSelected.filter(file => file.path !== item.path)
			} else {
				this.itemsSelected.push(item)
			}
		},

		async goToPath(item) {
			if (item.type !== "folder") return
			this.prefix = item.path
			const foundPathHistory = this.findPathHistory(this.prefix)
			if (foundPathHistory) {
				this.files = foundPathHistory?.files
			} else {
				await this.fetchFiles()
			}
			this.addToPathHistory({ prefix: this.prefix, files: this.files })
		},

		async pathClick(path) {
			this.prefix = path ? path.key : ""
			const foundPathHistory = this.findPathHistory(this.prefix)
			if (foundPathHistory) {
				this.files = foundPathHistory?.files
			} else {
				await this.fetchFiles()
			}
			this.addToPathHistory({ prefix: this.prefix, files: this.files })
		},
		toggleCreateFolderModal() {
			this.createFolderName = ""
			this.isCreateFolderModalOpen = !this.isCreateFolderModalOpen
		},
		toggleUploadFileModal() {
			this.isUploadModalOpen = !this.isUploadModalOpen
		},
		toggleDeleteModal() {
			this.isDeleteModalOpen = !this.isDeleteModalOpen
			this.deleteConfirmText = null
		},
		toggleUpdateFileModal() {
			this.isUpdateFileModalOpen = !this.isUpdateFileModalOpen
		},
		toggleSearch() {
			this.isSearching = !this.isSearching
			this.selectItem = null
		},
		async fetchFiles() {
			this.fileLoading = true
			this.selectItem = null
			this.clearPathHistory()
			this.files = await this.fileListing()
			this.fileLoading = false
		},
		onDelete(item) {
			this.selectItem = item
			this.toggleDeleteModal()
		},
		async onDeleteConfirm() {
			if (this.selectItem.type === "folder") {
				await this.deleteFolder(this.selectItem.path.slice(0, this.selectItem.path.length - 1))
			} else {
				await this.deleteFile(this.selectItem.path)
			}
			this.toggleDeleteModal()
			this.clearPathHistory()
			await this.fetchFiles()
		},
		onEdit(item) {
			this.selectItem = item
			if (item.type === "file") {
				this.toggleUpdateFileModal()
			}
		},
		async afterFileUpdate(file) {
			let newKey = this.selectItem.path.split("/")
			newKey.splice(-1, 1)
			newKey.push(file.fileName)
			newKey = newKey.join("/")
			if (file.isPublic !== this.selectItem.isPublic) {
				await this.setFileAcl(this.selectItem.path, file.isPublic)
			}
			if (this.selectItem.path !== newKey) {
				await this.moveFile(this.selectItem.path, newKey)
			}
			this.toggleUpdateFileModal()
			this.clearPathHistory()
			await this.fetchFiles()
		},
		onCut(item) {
			this.cutItem = item
			item.isCut = true
			this.$forceUpdate()
		},
		async onPaste(newPath) {
			const newKey = newPath.split("/")
			newKey.splice(-1, 1)
			newKey.push(this.cutItem.name)
			await this.moveFile(this.cutItem.path, newKey.join("/"))
			this.cutItem = null
			this.clearPathHistory()
			await this.fetchFiles()
		},
		async handleFileSelectFromSearch(obj) {
			let keys = obj?.key?.split("/")
			keys.pop()
			this.prefix = keys?.map(item => item + "/")?.join("")
			this.isSearching = false
			await this.fetchFiles()
			this.selectItem = {
				name: obj?.fileName,
				path: obj?.key,
			}
		},
		handleItemsSelectedFromSearch(items) {
			this.itemsSelected = items.map(item => ({
				type: "file",
				path: item.key,
				name: item.fileName,
				...item,
			}))
		},
		async fileListing() {
			const sortString = this.sortField && this.sortDirection ? `${this.sortField}_${this.sortDirection}` : `${this.sortField}`

			const commonPrefixes = []
			const objectSummaries = {}

			const PageSize = 1000
			let pageIndex = 0
			let page
			do {
				page = await api({
					query: query.fileListing,
					variables: {
						prefix: this.prefix,
						bucketName: this.bucketName,
						sort: sortString,
						skip: PageSize * pageIndex++,
						limit: PageSize,
					},
				})

				if (page?.data?.fileListing) {
					if (page.data.fileListing.commonPrefixes) {
						commonPrefixes.push(...page.data.fileListing.commonPrefixes)
					}
					if (page.data.fileListing.objectSummaries) {
						page.data.fileListing.objectSummaries.forEach(e => (objectSummaries[e.key] = e))
					}
				}
			} while (page?.data?.fileListing?.objectSummaries?.length === PageSize)

			return {
				commonPrefixes: [...new Set(commonPrefixes)],
				objectSummaries: Object.values(objectSummaries),
			}
		},
		async deleteFile(key) {
			const variables = {
				key,
			}
			const res = await api({ query: mutation.deleteFile, variables })
			if (res?.data?.deleteFile?.status === "success") {
				this.$store.dispatch("addToastMessage", {
					type: "success",
					content: { type: "message", text: this.$t("core.fileDeleted") },
				})
			} else {
				this.$store.dispatch("addToastMessage", {
					type: "error",
					content: {
						type: "message",
						text: res?.data?.deleteFile?.errors || res?.data?.deleteFile?.errors_2,
					},
				})
			}
		},
		async moveFile(oldKey, newKey) {
			const variables = {
				key: oldKey,
				newKey,
			}
			const res = await api({ query: mutation.moveFile, variables })
			if (res?.data?.moveFile?.status === "success") {
				this.$store.dispatch("addToastMessage", {
					type: "success",
					content: { type: "message", text: this.$t("core.fileUpdated") },
				})
			} else {
				this.$store.dispatch("addToastMessage", {
					type: "error",
					content: {
						type: "message",
						text: res?.data?.moveFile?.errors || res?.data?.moveFile?.errors_2,
					},
				})
			}
		},
		async setFileAcl(key, isPublic) {
			const variables = { key, isPublic }
			const res = await api({ query: mutation.setFileAcl, variables })
			if (res?.data?.setFileAcl?.status === "success") {
				this.$store.dispatch("addToastMessage", {
					type: "success",
					content: { type: "message", text: this.$t("core.fileUpdated") },
				})
			} else {
				this.$store.dispatch("addToastMessage", {
					type: "error",
					content: {
						type: "message",
						text: res?.data?.setFileAcl?.errors || res?.data?.setFileAcl?.errors_2,
					},
				})
			}
		},
		async deleteFolder(key) {
			const variables = {
				key,
			}
			const res = await api({ query: mutation.deleteFolder, variables })
			if (res?.data?.deleteFolder?.status === "success") {
				this.$store.dispatch("addToastMessage", {
					type: "success",
					content: {
						type: "message",
						text: this.$t("core.folderDeleted"),
					},
				})
			} else {
				this.$store.dispatch("addToastMessage", {
					type: "error",
					content: {
						type: "message",
						text: res?.data?.deleteFolder?.errors || res?.data?.deleteFolder?.errors_2,
					},
				})
			}
		},
		async createFolder() {
			const variables = {
				key: this.prefix + this.createFolderName,
			}
			const res = await api({ query: mutation.createFolder, variables })
			if (res?.data?.createFolder?.status === "success") {
				this.$store.dispatch("addToastMessage", {
					type: "success",
					content: {
						type: "message",
						text: this.$t("core.folderCreated"),
					},
				})
			} else {
				this.$store.dispatch("addToastMessage", {
					type: "error",
					content: {
						type: "message",
						text: res?.data?.createFolder?.errors || res?.data?.createFolder?.errors_2,
					},
				})
			}
			this.toggleCreateFolderModal()
			this.fetchFiles()
		},
		async downloadFile(item) {
			const endpoint = window?.envConfig?.COVERGO_FILE_SYSTEM

			const blob = await axios({
				url: `${endpoint}/${encodeURIComponent(item.path)}`,
				method: "GET",
				headers: {
					Authorization: `Bearer ${this?.$store?.state?.auth?.token}`,
				},
				responseType: "blob",
			})

			const link = document.createElement("a")
			link.href = window.URL.createObjectURL(blob.data)
			link.download = item.name
			link.click()
		},
		afterFileUpload() {
			this.toggleUploadFileModal()
			this.clearPathHistory()
			this.fetchFiles()
		},
		onPickFile() {
			if (this.itemsSelected.length === 1) {
				this.$emit("file:selected", this.itemsSelected[0])
			} else {
				this.$emit("file:multiSelected", this.itemsSelected)
			}
		},
		clearPathHistory() {
			this.pathHistory = []
		},
		addToPathHistory({ prefix, files }) {
			if (this.pathHistory?.length === this.maxPathHistory) {
				this.pathHistory?.shift()
				this.pathHistory?.push({ prefix, files })
			} else this.pathHistory?.push({ prefix, files })
		},
		findPathHistory(prefix) {
			return this.pathHistory?.find(obj => obj.prefix === prefix)
		},
		formatBytes(bytes) {
			if (bytes === 0) return "0 Bytes"
			const k = 1024
			const sizes = ["B", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"]
			const i = Math.floor(Math.log(bytes) / Math.log(k))
			return parseFloat((bytes / k ** i).toFixed(2)) + " " + sizes[i]
		},
		async downloadAsZip() {
			// 1. Validate selection
			if (!this.itemsSelected.length) {
				this.$store.dispatch("addToastMessage", {
					type: "error",
					content: { type: "message", text: this.$t("core.noFilesSelected") },
				})
				return
			}

			// 2. Extract file keys from selected items
			const fileKeys = this.itemsSelected.map(item => item.path)

			// 3. Show loading state
			this.isZipDownloading = true

			try {
				// 4. Call createZipJob mutation
				const createRes = await api({
					query: mutation.createZipJob,
					variables: { input: { fileKeys } },
				})

				// 5. Handle creation error
				const jobId = createRes?.data?.zipJobMutationCreateZipJob?.jobId
				const errorMessage = createRes?.data?.zipJobMutationCreateZipJob?.errorMessage

				if (!jobId) {
					this.$store.dispatch("addToastMessage", {
						type: "error",
						content: {
							type: "message",
							text: errorMessage || this.$t("core.zipJobCreationFailed"),
						},
					})
					this.isZipDownloading = false
					return
				}

				// 6. Poll for job status (1 second interval)
				const downloadUrl = await this.pollZipJobStatus(jobId)

				// 7. Trigger download or show error
				if (downloadUrl) {
					window.open(downloadUrl, "_blank")
					this.$store.dispatch("addToastMessage", {
						type: "success",
						content: { type: "message", text: this.$t("core.zipDownloadReady") },
					})
				}
			} catch (error) {
				this.$store.dispatch("addToastMessage", {
					type: "error",
					content: {
						type: "message",
						text: error?.message || this.$t("core.zipDownloadFailed"),
					},
				})
			} finally {
				this.isZipDownloading = false
			}
		},

		async pollZipJobStatus(jobId) {
			const maxAttempts = 600 // ~10 minute timeout
			let attempts = 0

			while (attempts < maxAttempts) {
				const res = await api({
					query: query.getZipJobStatus,
					variables: { id: jobId },
				})

				const job = res?.data?.zipJobQueryZipJob

				if (job?.status === "Completed" && job?.downloadUrl) {
					return job.downloadUrl
				}

				if (job?.status === "Failed" || job?.errorMessage) {
					this.$store.dispatch("addToastMessage", {
						type: "error",
						content: {
							type: "message",
							text: job?.errorMessage || this.$t("core.zipJobFailed"),
						},
					})
					return null
				}

				await new Promise(r => setTimeout(r, 1000)) // 1 second delay
				attempts++
			}

			// Timeout error
			this.$store.dispatch("addToastMessage", {
				type: "error",
				content: {
					type: "message",
					text: this.$t("core.zipJobTimeout"),
				},
			})
			return null
		},
	},
}
</script>
